import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {renderReportSidePanelCloseButton} from '../../reportSidePanel/reportSidePanelHelpers';

/*
 * Props.
 */

interface KnowledgeBaseReportSidePanelHeaderProps {
  title: React.ReactNode;
  shouldDisplayBackButton?: boolean;
  onRequestClose: () => void;
  onBackClick: () => void;
  onEdit?: () => void;
  onView?: () => void;
}

/*
 * Style.
 */

const StyledHeaderWrapperDiv = styled.div`
  ${flexCenter}
  min-height: 56px;
  padding: 0 16px;
  color: ${(p) => p.theme.greys.shade70};
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
  overflow-y: hidden;
  gap: 8px;
`;

const StyledHeaderTitle = styled.div`
  flex: 1;
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.medium};
`;

const StyledSimpleDropdownItemContentDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  margin-right: 12px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportSidePanelHeader: FC<KnowledgeBaseReportSidePanelHeaderProps> = ({
  title,
  shouldDisplayBackButton,
  onRequestClose,
  onBackClick,
  onEdit,
  onView,
}) => (
  <StyledHeaderWrapperDiv>
    {shouldDisplayBackButton && <IconButton iconName="chevronLeftSmall" label={null} onClick={onBackClick} />}
    <StyledHeaderTitle>{title}</StyledHeaderTitle>
    {maybeRenderReportSidePanelMoreActionsButton(onEdit, onView)}
    {renderReportSidePanelCloseButton(onRequestClose)}
  </StyledHeaderWrapperDiv>
);

function maybeRenderReportSidePanelMoreActionsButton(onEdit?: () => void, onView?: () => void) {
  if (!onEdit && !onView) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={() => (
        <IconButton
          iconName="moreVertical"
          label={<Trans id="knowledge-base-side-panel-more-actions">More</Trans>}
        />
      )}
      renderModal={(popoverProps: PopoverTransferProps) => (
        <SimpleDropdown {...popoverProps}>
          {onEdit && (
            <SimpleDropdownItem onSelect={onEdit}>
              <StyledSimpleDropdownItemContentDiv>
                <StyledIcon name="editSmall" />
                <Trans id="knowledge-base-side-panel-more-actions-edit">Edit article</Trans>
              </StyledSimpleDropdownItemContentDiv>
            </SimpleDropdownItem>
          )}
          {onView && (
            <SimpleDropdownItem onSelect={onView}>
              <StyledSimpleDropdownItemContentDiv>
                <StyledIcon name="externalLinkSmall" />
                <Trans id="knowledge-base-side-panel-more-actions-view">View article</Trans>
              </StyledSimpleDropdownItemContentDiv>
            </SimpleDropdownItem>
          )}
        </SimpleDropdown>
      )}
    />
  );
}
